/**
 * ProductTour
 * ===============
 * 5-step guided walkthrough that fires on first authed login.
 * Pairs with the seed-sample flow — if the user has no data yet, the
 * tour auto-triggers a seed first so every step has something to point at.
 *
 * Persistence:
 *   localStorage["cartie_tour_completed_v1"] = "1"   → never show again
 *   localStorage["cartie_tour_dismissed_at"]         → for cooldown logic
 *
 * Brand: Slate-950 popovers, emerald accents, soft amber for warnings.
 */
import React, { useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import axios from 'axios';
import { toast } from 'sonner';

const STORAGE_KEY = 'cartie_tour_completed_v1';
const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Steps reference real DOM elements via CSS selector. Each step routes
 *  to the page where the highlighted element lives. */
const STEPS = [
  {
    route: '/dashboard',
    selector: '[data-tour="dashboard-hero"]',
    popover: {
      title: 'Welcome to CARTIE 🎉',
      description: "Your Financial OS for cloud + AI. We'll show you the 5 most useful surfaces in 60 seconds. Skip anytime.",
      side: 'bottom',
      align: 'center',
    },
  },
  {
    route: '/customer-pl',
    selector: '[data-tour="pl-table"]',
    popover: {
      title: 'Customer P&L',
      description: "See exactly which customers cost more than they pay you. We pull cloud spend ↔ Stripe revenue and surface the bleeders. The worst-margin customer is right at the top.",
      side: 'top',
      align: 'center',
    },
  },
  {
    route: '/ai-feature-profitability',
    selector: '[data-tour="ai-feature-grid"]',
    popover: {
      title: 'AI Feature Profitability',
      description: "Same math, per-feature: chat / summarise / agent / embeddings. The same `BurnRate Co` that bleeds in your P&L? They're also the heaviest user of `summarise` (your worst-margin feature). One coherent story.",
      side: 'top',
    },
  },
  {
    route: '/decision-simulator',
    selector: '[data-tour="ds-scenarios"]',
    popover: {
      title: 'Decision Simulator',
      description: "6 what-if scenarios with deterministic math: swap LLM, increase SP coverage, add caching, drop unprofitable customer, cap token budget. CARTIE ranks them by $ impact × risk × reversibility.",
      side: 'top',
    },
  },
  {
    route: '/settings',
    selector: '[data-tour="invite-team"]',
    popover: {
      title: 'Invite your team',
      description: "FinOps wins are won when finance and engineering see the same numbers. Add teammates here — we'll email them a magic link.",
      side: 'top',
    },
  },
];

/** Ensure the user has data to look at — silently calls the seed-sample
 *  endpoint if their tenant is empty so step 2 actually has rows. */
async function ensureSeed() {
  try {
    const tok = localStorage.getItem('token');
    if (!tok) return;
    const headers = { Authorization: `Bearer ${tok}` };
    const { data: status } = await axios.get(`${API_URL}/onboarding/status`, { headers });
    if (status?.has_any_data) return;
    await axios.post(`${API_URL}/onboarding/seed-sample`, {}, { headers });
    toast.success('Sample data seeded so the tour has something to show.');
  } catch (_) { /* tour proceeds even if seed fails */ }
}

const ProductTour = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const driverRef = useRef(null);
  const startedRef = useRef(false);

  const start = useCallback(async () => {
    if (startedRef.current) return;
    startedRef.current = true;

    await ensureSeed();

    const driverObj = driver({
      animate: true,
      smoothScroll: true,
      allowClose: true,
      overlayColor: 'rgba(2, 6, 23, 0.85)',  // slate-950/85
      stagePadding: 4,
      progressText: 'Step {{current}} of {{total}}',
      nextBtnText: 'Next →',
      prevBtnText: '← Back',
      doneBtnText: 'All done — let me explore',
      onCloseClick: () => { dismiss(); driverObj.destroy(); },
      onDestroyed: () => { startedRef.current = false; },
    });

    driverRef.current = driverObj;

    // Driver.js needs steps in the order they'll be visited. Each step
    // auto-navigates BEFORE it's shown using onHighlightStarted.
    const driverSteps = STEPS.map((s, idx) => ({
      element: s.selector,
      popover: {
        ...s.popover,
        showButtons: ['next', 'previous', 'close'],
      },
      onHighlightStarted: async (el, _step) => {
        if (location.pathname !== s.route) {
          navigate(s.route);
          // Give React + the page a moment to mount the highlighted node.
          await new Promise((r) => setTimeout(r, 800));
        }
        // If the targeted element doesn't exist yet, fall back to body.
        if (!document.querySelector(s.selector)) {
          // Force-update the step's element to body so popover still shows.
          driverObj.setSteps(driverObj.getConfig().steps.map((st, i) => i === idx
            ? { ...st, element: 'body' }
            : st));
        }
      },
    }));

    driverObj.setSteps(driverSteps);
    driverObj.drive();

    // Mark completion when the user finishes the last step.
    const orig = driverObj.destroy;
    driverObj.destroy = (...args) => {
      try {
        const isLast = driverObj.isLastStep();
        if (isLast) localStorage.setItem(STORAGE_KEY, '1');
      } catch (_) { /* */ }
      return orig.apply(driverObj, args);
    };
  }, [location.pathname, navigate]);

  const dismiss = useCallback(() => {
    localStorage.setItem('cartie_tour_dismissed_at', new Date().toISOString());
  }, []);

  // Auto-start on first authed mount where tour hasn't been completed AND
  // the user is on /dashboard. We don't yank them away from other pages —
  // if they navigate to /customer-pl, /anomalies, /causal-bot, etc., they
  // are clearly exploring on their own and don't want a tour interruption.
  useEffect(() => {
    const completed = localStorage.getItem(STORAGE_KEY);
    if (completed) return;
    const tok = localStorage.getItem('token');
    if (!tok) return;
    if (location.pathname !== '/dashboard') return;
    // Defer 1.5s so the layout settles first.
    const t = setTimeout(start, 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return null;
};

/** Imperative restart — wired to the user-menu "Replay product tour" item. */
export function startProductTour() {
  localStorage.removeItem(STORAGE_KEY);
  // The mounted ProductTour will re-run on next route change. To force
  // an immediate restart, we synthesize a click on a hidden trigger.
  window.dispatchEvent(new CustomEvent('cartie:start-tour'));
}

export default ProductTour;
